// Shared utilities for admin pages — LIGHT MODE.
import api from '@/lib/api';

export const adminApi = {
  overview: () => api.get('/admin/overview').then(r => r.data),
  users:    (params) => api.get('/admin/users', { params }).then(r => r.data),
  createUser: (body) => api.post('/admin/users', body).then(r => r.data),
  updateUser: (id, body) => api.patch(`/admin/users/${id}`, body).then(r => r.data),
  resetPwd:   (id, newPassword) => api.post(`/admin/users/${id}/reset-password`, { newPassword }).then(r => r.data),
  unlockUser: (id) => api.post(`/admin/users/${id}/unlock`).then(r => r.data),
  deactivateUser: (id) => api.delete(`/admin/users/${id}`).then(r => r.data),
  activateUser:   (id) => api.patch(`/admin/users/${id}`, { isActive: true }).then(r => r.data),
  projects: (params) => api.get('/admin/projects', { params }).then(r => r.data),
  updateProject: (id, body) => api.patch(`/admin/projects/${id}`, body).then(r => r.data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`).then(r => r.data),
  modules: () => api.get('/admin/modules').then(r => r.data),
  updateModule: (code, body) => api.patch(`/admin/modules/${code}`, body).then(r => r.data),
  roles:   () => api.get('/admin/role-templates').then(r => r.data),
  updateRole: (code, body) => api.patch(`/admin/role-templates/${code}`, body).then(r => r.data),
  audit:   (params) => api.get('/admin/audit', { params }).then(r => r.data),
  invitations: () => api.get('/admin/invitations').then(r => r.data),
  resendInvite: (body) => api.post('/admin/invitations/resend', body).then(r => r.data),
  integrations: () => api.get('/admin/integrations').then(r => r.data),
  integrationKeys: () => api.get('/admin/integration-keys').then(r => r.data),
  saveIntegrationKey: (env, value) => api.put(`/admin/integration-keys/${env}`, { value }).then(r => r.data),
  clearIntegrationKey: (env) => api.delete(`/admin/integration-keys/${env}`).then(r => r.data),
  revealIntegrationKey: (env) => api.get(`/admin/integration-keys/${env}/reveal`).then(r => r.data),
  testIntegration: (serviceId, values) => api.post(`/admin/integration-keys/${serviceId}/test`, { values }).then(r => r.data),
  flags:   () => api.get('/admin/feature-flags').then(r => r.data),
  toggleFlag: (key, enabled) => api.patch(`/admin/feature-flags/${key}`, { enabled }).then(r => r.data),
  license: () => api.get('/admin/license').then(r => r.data),
  updateLicense: (body) => api.patch('/admin/license', body).then(r => r.data),
  tenants: () => api.get('/admin/tenants').then(r => r.data),
};

export const Card = ({ children, className = '', ...rest }) => (
  <div className={`rounded-xl border border-border bg-card text-card-foreground shadow-sm ${className}`} {...rest}>
    {children}
  </div>
);

export const Badge = ({ tone = 'slate', children, ...rest }) => {
  const map = {
    slate:  'bg-slate-100 text-slate-700 border-slate-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    red:    'bg-red-50 text-red-700 border-red-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return <span {...rest} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${map[tone] || map.slate}`}>{children}</span>;
};

export const StatTile = ({ label, value, sub, accent = 'slate' }) => {
  const colorMap = { slate: '#334155', orange: '#F26B1F', blue: '#1E4E8C', green: '#10B981', red: '#DC2626' };
  return (
    <Card className="p-5 hover:shadow-md transition-shadow">
      <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-2">{label}</div>
      <div className="text-3xl font-light tabular-nums" style={{ color: colorMap[accent] }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
};

export const Table = ({ columns, rows, empty = 'No records found', getKey = (_, i) => i }) => (
  <Card className="overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left px-4 py-3 text-[10px] font-medium tracking-[0.2em] text-slate-500 uppercase whitespace-nowrap" style={c.thStyle}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-slate-400 text-sm">{empty}</td></tr>
          ) : rows.map((row, i) => (
            <tr key={getKey(row, i)} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-slate-700 align-middle" style={c.tdStyle}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

export const Toolbar = ({ children, className = '' }) => (
  <div className={`flex flex-wrap items-center gap-3 mb-6 ${className}`}>{children}</div>
);

export const inpCls    = "px-3 py-2 rounded-md text-sm bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]/30 focus:border-[#1E4E8C] placeholder:text-slate-400 transition";
export const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] tracking-[0.1em] uppercase font-semibold bg-[#F26B1F] hover:bg-[#D85A0F] active:scale-[0.98] text-white transition shadow-sm";
export const btnGhost   = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-wide text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition";
export const btnDanger  = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-wide text-red-600 hover:text-red-700 hover:bg-red-50 transition";
export const btnSuccess = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-wide text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition";
